import React, { useState, useEffect, useCallback } from 'react';
import { Activity, ExternalLink } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * Launch-day Platform Health Card.
 *
 * Renders the live output of GET /api/status/summary so admins can see, at a
 * glance, that every backbone component is green during launch. Polls every
 * 30 seconds. Object Storage is the headliner here — Sprint 187 made it
 * canonical for AvA portraits, charters, social posts, and email
 * attachments, so a red dot on Object Storage is a Tier-1 launch incident.
 *
 * Clicking the card opens the public /status page in a new tab so the user
 * can drill into incident history.
 */

const STATUS_COLOR = {
  operational: '#10B981',
  degraded: '#F59E0B',
  outage: '#EF4444',
  unknown: '#9CA3AF',
};

const STATUS_LABEL = {
  operational: 'Operational',
  degraded: 'Degraded',
  outage: 'Outage',
  unknown: 'Unknown',
};

const POLL_MS = 30_000;

const PlatformHealthCard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/status/summary');
      setSummary(data);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_MS);
    return () => clearInterval(id);
  }, [fetchSummary]);

  if (loading) {
    return (
      <div
        className="bg-white p-5 rounded-lg"
        style={{ border: '1px solid #E0E0E0' }}
        data-testid="platform-health-card-loading"
      >
        <div className="flex items-center gap-2">
          <Activity size={16} style={{ color: '#9CA3AF' }} />
          <span className="text-sm" style={{ color: '#9CA3AF' }}>Loading platform health…</span>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div
        className="bg-white p-5 rounded-lg"
        style={{ border: '1px solid #FCA5A5' }}
        data-testid="platform-health-card-error"
      >
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLOR.outage }} />
          <span className="text-sm font-medium" style={{ color: '#1A1209' }}>Platform health probe failed</span>
        </div>
        <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>{error || 'No response from /api/status/summary'}</p>
      </div>
    );
  }

  const overall = summary.overall || 'unknown';
  const components = summary.components || [];
  const checked = summary.checked_at ? new Date(summary.checked_at) : null;

  return (
    <div
      className="bg-white p-5 rounded-lg"
      style={{ border: '1px solid #E0E0E0' }}
      data-testid="platform-health-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={16} style={{ color: STATUS_COLOR[overall] }} />
          <h3 className="text-sm font-semibold" style={{ color: '#1A1209' }}>Platform Health</h3>
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
            style={{ backgroundColor: `${STATUS_COLOR[overall]}1A`, color: STATUS_COLOR[overall] }}
            data-testid="platform-health-overall-pill"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[overall] }} />
            {summary.overall_label || STATUS_LABEL[overall]}
          </span>
        </div>
        <a
          href="/status"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-[11px] hover:underline"
          style={{ color: '#6B7280' }}
          data-testid="platform-health-public-link"
        >
          Public status <ExternalLink size={11} />
        </a>
      </div>

      {/* Components grid */}
      <div className="space-y-1.5">
        {components.map((c) => {
          const statusColor = STATUS_COLOR[c.status] || STATUS_COLOR.unknown;
          const isStorage = c.name === 'Object Storage';
          return (
            <div
              key={c.name}
              className="flex items-center justify-between py-1.5 px-2 rounded"
              style={{
                backgroundColor: isStorage ? '#FFF7ED' : 'transparent',
                borderLeft: isStorage ? `2px solid ${statusColor}` : 'none',
              }}
              data-testid={`platform-health-component-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
                <span className="text-[13px] font-medium" style={{ color: '#1A1209' }}>{c.name}</span>
                {isStorage && (
                  <span
                    className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: '#F5831F', color: 'white' }}
                  >
                    new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11px]" style={{ color: '#6B7280' }}>
                {Number.isFinite(c.ping_ms) && c.ping_ms > 0 && (
                  <span data-testid={`platform-health-ping-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {c.ping_ms}ms
                  </span>
                )}
                <span style={{ color: statusColor, fontWeight: 600 }}>
                  {STATUS_LABEL[c.status] || c.status}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
        <span className="text-[10px]" style={{ color: '#9CA3AF' }}>
          {summary.incidents_24h > 0
            ? `${summary.incidents_24h} incident${summary.incidents_24h === 1 ? '' : 's'} · 24h`
            : 'No incidents · 24h'}
        </span>
        <span className="text-[10px]" style={{ color: '#9CA3AF' }} data-testid="platform-health-checked-at">
          {checked ? `Checked ${checked.toLocaleTimeString()}` : 'Checking…'}
        </span>
      </div>
    </div>
  );
};

export default PlatformHealthCard;
